import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { CATEGORY_LABELS } from '../constants/categories';

const COLORS = {
  views: '#3b82f6',      // Blue
  favorites: '#ef4444',  // Red
  confirmations: '#22c55e' // Green
};

export const EngagementPieChart = ({ events }) => {
  // Calculate totals from real Firestore data
  const totalViews = events.reduce((sum, e) => sum + (e.views || 0), 0);
  const totalFavorites = events.reduce((sum, e) => sum + (e.favoriteUsers?.length || 0), 0);
  const totalConfirmations = events.reduce((sum, e) => sum + (e.attendeesCount || 0), 0);

  const data = [
    { name: 'Visualizações', value: totalViews, color: COLORS.views },
    { name: 'Favoritos', value: totalFavorites, color: COLORS.favorites },
    { name: 'Confirmações', value: totalConfirmations, color: COLORS.confirmations }
  ].filter(item => item.value > 0); // Only show items with values

  if (data.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Distribuição de Engajamento</h3>
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          <p>Sem dados de engajamento ainda</p>
        </div>
      </div>
    );
  }

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        className="text-xs font-semibold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <h3 className="text-lg font-semibold mb-4">Distribuição de Engajamento</h3>
      <div className="w-full" style={{ minHeight: '350px' }}>
        <ResponsiveContainer width="100%" height={350}>
          <PieChart margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={CustomLabel}
              outerRadius={90}
              fill="#8884d8"
              dataKey="value"
              animationBegin={0}
              animationDuration={800}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                padding: '8px 12px'
              }}
            />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              wrapperStyle={{ paddingTop: '20px' }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const HighlightedPieChart = ({ events }) => {
  const highlighted = events.filter(e => e.isHighlighted);
  const normal = events.filter(e => !e.isHighlighted);

  // Calculate engagement for highlighted events
  const highlightedEngagement = 
    highlighted.reduce((sum, e) => sum + (e.views || 0) + (e.favoriteUsers?.length || 0) + (e.attendeesCount || 0), 0);
  
  // Calculate engagement for normal events
  const normalEngagement = 
    normal.reduce((sum, e) => sum + (e.views || 0) + (e.favoriteUsers?.length || 0) + (e.attendeesCount || 0), 0);

  const data = [
    { name: `Impulsionados (${highlighted.length})`, value: highlightedEngagement, color: '#f59e0b' },
    { name: `Normais (${normal.length})`, value: normalEngagement, color: '#6366f1' }
  ].filter(item => item.value > 0);

  if (data.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Destacados vs Normais</h3>
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          <p>Sem eventos para comparar</p>
        </div>
      </div>
    );
  }

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        className="text-xs font-semibold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <h3 className="text-lg font-semibold mb-4">Destacados vs Normais</h3>
      <div className="w-full" style={{ minHeight: '350px' }}>
        <ResponsiveContainer width="100%" height={350}>
          <PieChart margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={CustomLabel}
              outerRadius={90}
              fill="#8884d8"
              dataKey="value"
              animationBegin={0}
              animationDuration={800}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                padding: '8px 12px'
              }}
              formatter={(value) => [`${value} interações`, '']}
            />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              wrapperStyle={{ paddingTop: '20px' }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const TopEventsTable = ({ events }) => {
  // Sort by views and take top 5
  const topEvents = [...events]
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, 5);

  if (topEvents.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Top 5 Eventos</h3>
        <p className="text-muted-foreground text-center py-8">Nenhum evento encontrado</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <h3 className="text-lg font-semibold mb-4">Top 5 Eventos por Visualizações</h3>
      <div className="space-y-3">
        {topEvents.map((event, index) => {
          const views = event.views || 0;
          const favorites = event.favoriteUsers?.length || 0;
          const going = event.attendeesCount || 0;
          const totalEngagement = views + favorites + going;

          return (
            <div 
              key={event.id}
              className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
            >
              {/* Rank */}
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-bold text-primary">#{index + 1}</span>
              </div>

              {/* Event Info */}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold truncate">{event.title}</h4>
                <p className="text-sm text-muted-foreground">{CATEGORY_LABELS[event.category] ?? event.category} • {event.city}</p>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-xl font-bold text-blue-600">{views}</p>
                  <p className="text-xs text-muted-foreground">Views</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-red-600">{favorites}</p>
                  <p className="text-xs text-muted-foreground">Favoritos</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-green-600">{going}</p>
                  <p className="text-xs text-muted-foreground">Confirmados</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

